import { Component, OnInit, AfterViewInit, OnDestroy, Input, Output, EventEmitter, ChangeDetectorRef, ViewChild, ElementRef, TemplateRef, EmbeddedViewRef } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Overlay, OverlayRef, OverlayConfig } from '@angular/cdk/overlay';
import { TemplatePortalDirective } from '@angular/cdk/portal';

import { FieldComponent, UndoAction } from './field.component';
import { FormField, Disclaimer, Link } from './form-field.model';
import { DynamicOverlaysDirective } from './dynamic-overlays.directive';

interface FieldValue {
  displayed?: string,
  stored?: string,
  categories?: string,
  linked?: string,
  linked_categories?: string[],
  description?: string
}

@Component({
  selector: 'app-select-field',
  templateUrl: './select-field.component.html',
  styleUrls: ['./select-field.component.scss']
})
export class SelectFieldComponent extends FieldComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() blankOption: string;
  @Input() draftData: any;
  @Input() field: FormField;
  @Input() dynamicOverlays: DynamicOverlaysDirective;
  @Input() locked: boolean = false;
  @Input() lockValues: boolean = false;
  @Input() templateData: any;
  @Input() workflowId: number;
  @Output() disclaimerChanged = new EventEmitter<{field: string, value: any}>();
  @Output() focusChanged = new EventEmitter<boolean>();
  @Output() highlightChanged = new EventEmitter<{highlighted: boolean, color?: string}>();
  @Output() resized = new EventEmitter<FieldComponent>();
  @Output() returnKey = new EventEmitter<FieldComponent>();
  @Output() showDisclaimer = new EventEmitter<{field: string, disclaimer: Disclaimer}>();
  @Output() undoableAction = new EventEmitter<UndoAction>();
  @Output() updateLinked = new EventEmitter<{link: Link, value?: any, categories?: any[], enabled?: boolean}>();
  @Output() viewLoaded = new EventEmitter<FieldComponent>();
  @ViewChild("selectInput") selectInput: ElementRef;
  @ViewChild("versionTemplate") versionTemplate: TemplateRef<null>;
  @ViewChild("versionElement") versionElement: ElementRef;

  align: string;
  blankOptionVisible: boolean = true;
  categoryValues: FieldValue[] = [];
  defaultValue: FieldValue;
  disclaimer: Disclaimer;
  disclaimers: Disclaimer[];
  focused: boolean = false;
  id: string;
  highlightColor: string;
  highlightPattern: string;
  highlightValues: string[];
  highlighted: boolean = false;
  initialized: boolean = false;
  label: string;
  links: Link[];
  placeholder: string;
  previousValue: FieldValue;
  required: boolean = false;
  showingPreviousValue: boolean = false;
  value: FieldValue;
  values: FieldValue[];
  versionChanged: boolean = false;
  versionView: EmbeddedViewRef<null>;
  
  get blank(): boolean { return this.value == null }
  get touched(): boolean {return this.control != null && this.control.touched}
  get valid(): boolean {return this.locked == true || (this.control != null && this.control.valid)}

  constructor(
    cd: ChangeDetectorRef,
    private overlay: Overlay) { super(cd) }

  ngOnInit() {
    if (this.id == null) {this.id = this.field.id}
    if (this.placeholder == null) {this.placeholder = this.field.placeholder}
    this.label = this.field.label;
    this.required = this.field.required;
    this.values = this.field.values != null ? this.field.values : [];
    this.align = this.field.align;
    this.links = this.field.links;
    this.highlightColor = this.field.highlightColor;
    this.highlightPattern = this.field.highlightPattern;
    this.highlightValues = this.field.highlightValues;
    if (this.field.locked) { this.locked = true }
    this.disclaimers = this.field.disclaimers;
    if (this.field.blankOptionVisible != null) { this.blankOptionVisible = this.field.blankOptionVisible }

    this.loadDefaultValue();
    this.loadInitialState(this.draftData);
  }

  ngAfterViewInit() {
    this.viewLoaded.emit(this);
  }

  ngOnDestroy() {
    this.hidePreviousValue();
  }

  loadTemplateValue(value) {
    return (value instanceof Array) ? value[0] : value;
  }

  loadDefaultValue() {
    let templateValue: FieldValue;
    if (this.templateData != null) {
      templateValue = this.loadTemplateValue(this.templateData);
    }

    if (templateValue != null) {
      this.defaultValue = {
        stored: templateValue.stored,
        displayed: templateValue.displayed
      };
      if (this.lockValues) { this.locked = true };
    } else if (this.field.defaultValue != null) {
      let fieldValue = this.field.defaultValue;
      this.defaultValue = {
        stored: fieldValue.stored,
        displayed: fieldValue.displayed
      }
    }
  }

  loadInitialState(draftValue?: FieldValue) {
    let validators = [];
    if (this.required === true) {validators.push(Validators.required)}
    this.categoryValues = this.values;
    this.value = draftValue != null ? this.loadTemplateValue(draftValue) : this.defaultValue;
    let controlValue = this.value != null ? this.value.stored : null;
    this.control = new FormControl({value: controlValue, disabled: this.locked}, validators);
    this.control.valueChanges.subscribe((value) => {
      let newValue = this.values.find((v) => v.stored === value);
      this._dirty = this.initialized && !this.valuesEqual(newValue, this.value);
      this.value = newValue
      this.emitValue();
      if (this.initialized) {
        this.updateLinkedFields(true);
      }
      this.initialized = true;
   })
    // Emit initial validity and value
    this.control.updateValueAndValidity();
  }

  emitValue() {
    let value = this.value != null ? {
      displayed: this.value.displayed,
      stored: this.value.stored
    } : null
    this.valueChanged.emit({field: this.id, value: value});
    this.checkDisclaimers();
    this.checkHighlight();
  }

  setValue(value: any, emit: boolean = true) {
    this.value = value;
    if (this.control != null) {
      let controlValue = value != null ? value.stored : null;
      this.control.setValue(controlValue, {emitEvent: false});
    }
    if (emit) { this.emitValue(); }
  }

  setPreviousValue(value) {
    this.previousValue = value;
    this.versionChanged = !this.valuesEqual(this.value, this.previousValue);
    if (this.versionChanged) {
      this.showPreviousValue();
    }
    this.cd.markForCheck();
  }

  resetPreviousValue() {
    this.previousValue = null;
    this.versionChanged = false;
    this.hidePreviousValue();
    this.cd.markForCheck();
  }

  onFocus(event) { 
    if (this.locked) { return false }
    this.focused = true;
    this.focusChanged.emit(true);
    return true;
  }

  onBlur(event) {
    this.focused = false;
    this.focusChanged.emit(false);
    this.updateLinkedRows();
    return true;
  }

  reset() {
    this.loadInitialState();
    this.cd.markForCheck();
  }

  compare(other): number {
    let a = this.value != null && this.value.displayed != null ? this.value.displayed : '';
    let b = other.value != null && other.value.displayed != null ? other.value.displayed : '';
    if (a === '') {
      return 1;
    } else if (b === '') {
      return -1;
    } else {
      return a.localeCompare(b, 'us', {sensitivity: "base", ignorePunctuation: true});
    }
  }

  valuesEqual(a,b): boolean {
    a = a != null ? a.stored : null;
    b = b != null ? b.stored : null;
    return a == b;
  }

  checkDisclaimers() {
    if (this.disclaimers != null) {
      let value = this.value != null ? this.value.stored : null
      this.disclaimer = this.disclaimers.find((d) => d.value === value)
      this.disclaimerChanged.emit({field: this.id, value: this.disclaimer})
    }
  }

  onShowDisclaimer(event) {
    event.stopPropagation();
    this.showDisclaimer.emit({field: this.id, disclaimer: this.disclaimer})
  }  

  updateLinkedFields(userInitiated: boolean = false) {
    if (this.links != null) {
      for (let link of this.links) {
        if (link.row) { continue }
        if (this.values.length > 0 && this.values[0].hasOwnProperty('linked')) {
          if (userInitiated) {
            let value = this.value != null ? this.value.linked : null;
            this.updateLinked.emit({link: link, value: value})
          }
        } else if (this.values.length > 0 && this.values[0].hasOwnProperty('linked_categories')) {
          let categories = this.value != null ? this.value.linked_categories : null;
          this.updateLinked.emit({link: link, categories: categories})
        } else {
          this.updateLinked.emit({link: link, value: this.value})
        }
      }
    }
  }

  updateLinkedRows() {
    if (this.links != null) {
      for (let link of this.links) {
        if (link.row) {
          let enabled = false;
          if (link.presence) {
            enabled = (this.value != null)
          } else if (link.match && this.value != null) {
            enabled = (link.match.find((x) => x == this.value.stored) != null)
          }
          this.updateLinked.emit({link: link, enabled: enabled})
        }
      }
    }
  }

  setCategories(categories, emit: boolean = true) {
    if (categories != null) {
      this.categoryValues = this.values.filter((v) => {
        // if (v.categories == null) {return true};
        for (let category of categories) {
          for (let vc of v.categories) {
            if (vc === category) { return true }
          }
        }
        return false;
      })
    } else {
      this.categoryValues = this.values;
    }

    if (this.categoryValues.length <= 1) {
      this.enable();
      this.setValue(this.categoryValues[0], emit);
    } else {
      this.enable();
    }

    if (this.value != null) {
      if (this.categoryValues.find((v) => v.stored === this.value.stored) == null) {
        this.setValue(null, emit);
      }
    }
  }

  setValues(values) {
    this.values = values;
    this.setCategories(null);
    this.cd.markForCheck();
  }

  checkHighlight() {
    let value = this.value != null ? this.value.stored : '';
    if (this.highlightValues != null) {
      let match = false;
      for (let v of this.highlightValues) {
        if (v === value) {
          match = true;
          break;
        }
      }
      this.highlighted = match;
      this.highlightChanged.emit({highlighted: this.highlighted});
    } else if (this.highlightPattern != null) {
      let regex = new RegExp(this.highlightPattern);
      this.highlighted = regex.test(value);
      this.highlightChanged.emit({highlighted: this.highlighted});
    }
  }

  showPreviousValue() {
    this.showingPreviousValue = true;
    let container = this.dynamicOverlays.viewContainerRef;
    this.versionView = container.createEmbeddedView(this.versionTemplate);
    this.positionOverlays();
  }

  hidePreviousValue() {
    if (this.versionView != null) {
      this.versionView.destroy();
    }
    this.showingPreviousValue = false;
  }

  togglePreviousValue() {
    if (this.showingPreviousValue) {
      this.hidePreviousValue();
    } else {
      this.showPreviousValue();
    }
  }

  positionOverlays() {
    if (this.versionView != null) {
      let rect = this.versionElement.nativeElement.getBoundingClientRect();
      let rootNode = this.versionView.rootNodes.find((n) => n.tagName == 'DIV');
      rootNode.style.left = '' + (rect.left + window.pageXOffset) + 'px';
      rootNode.style.top = '' + (rect.top + window.pageYOffset) + 'px';
      rootNode.style.maxWidth = '' + rect.width + 'px';
    }
  }

  setPatternValue(field: string, value: any) { }

  undo(action: UndoAction) { }
}


// WEBPACK FOOTER //
// ./src/app/forms/select-field.component.ts